import { createSlice } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import {
  calculateAssessmentReadinessTotal,
  calculateReadinessTotal,
} from '../utils/readinessScoring';

/**
 * Borrower-flow step ids.
 *
 * The numeric values are kept for backwards compatibility with state already
 * persisted in localStorage / Supabase, but new code should use these named
 * constants instead of magic numbers (e.g. setCurrentStep(STEPS.UNDERWRITING)
 * instead of setCurrentStep(3.5)).
 *
 * MVP path: ASSESSMENT → APPLICATION → DOCUMENTS → UNDERWRITING.
 * Phase-2 steps (HUMAN_UNDERWRITING, LENDER_NETWORK, LENDER_SUBMISSION) are
 * feature-flagged and unreachable from the borrower flow today.
 */
export const STEPS = {
  ASSESSMENT:           0,    // ProductRecommendationFlow (assessment + recommendations)
  APPLICATION:          1,    // URLAApplication (smart-subset URLA)
  DOCUMENTS:            2,    // DocumentPreparation (dynamic doc list)
  UNDERWRITING_PICKER:  3,    // SubmitToUnderwriter (Phase 2: AI vs human picker)
  UNDERWRITING:         3.5,  // OwenUnderwritingVerdict (Owen's verdict — MVP terminal step)
  HUMAN_UNDERWRITING:   3.7,  // HumanUnderwritingFlow (Phase 2)
  LENDER_NETWORK:       4,    // LenderNetworkSelection (Phase 2)
  LENDER_SUBMISSION:    5,    // LenderSubmission (Phase 2)
};

const initialState = {
  currentStep: STEPS.ASSESSMENT,
  readinessScore: 0,
  
  // User authentication
  user: null, // { email, firstName, lastName, authMethod }
  isAuthenticated: false,
  
  // Document path choice
  documentPath: null, // 'free-checklist' or 'early-upload'
  documentUploadFee: 99, // Small fee, refunded by lender
  documentsUploaded: [],
  
  // Lender network
  anonymousSubmission: false,
  interestedLenders: [],
  selectedLender: null,
  
  // Underwriting
  underwritingComplete: false,
  underwritingReport: null,
  
  // Product recommendation answers
  productRecommendation: {
    purchaseOrRefinance: '',
    refiGoal: '',
    propertyType: '',
    propertyUse: '',
    propertyState: '',
    propertyCounty: '',
    propertyZip: '',
    estimatedValue: '',
    downPayment: '',
    creditScore: '',
    loanSizeTier: '',
    employmentStatus: '',
    annualIncome: '',
    monthlyDebts: '',
    firstTimeHomeBuyer: '',
    militaryService: '',
    ruralProperty: '',
    existingUSDALoan: '',
    priorNADL: '',
    stateHfaParticipant: '',
    tribalHousingPath: '',
    affordableProgramInterest: '',
    renovationNeeds: '',
    agencyLoanServicer: '',
    refiNowLowIncomeHint: '',
    incomeDocumentation: '',
    nonQmFlexInterest: '',
    loanTermPreference: '',
    rateTypePreference: '',
    planToKeepLoan: '',
    refinanceType: '',
  },
  
  // Recommended product
  recommendedProduct: null,
  recommendedProducts: [], // Multiple recommendations
  
  // Full application data
  applicationData: {
    borrowerInfo: { entries: [] },
    contactInfo: {},
    currentAddress: {},
    previousAddress: {},
    personalInfo: {},
    propertyInfo: {},
    loanInfo: {},
    financialInfo: {},
    employmentIncome: { entries: [] },
    otherIncome: { entries: [] },
    employmentInfo: {},
    assets: { entries: [] },
    credits: { entries: [] },
    giftsGrants: { entries: [] },
    liabilities: { entries: [] },
    realEstateOwned: { entries: [] },
    declarations: {},
    militaryService: {},
    demographics: {},
    acknowledgment: {},
  },
  
  // Documents uploaded
  documents: [],
  
  // Underwriting choice
  underwritingChoice: null, // 'ai' or 'human' or null
  
  // Submission status
  submittedToLenders: false,
  selectedLenders: [],
  
  // Save timestamp
  lastSaved: null,

  // Guest save (email-only, no auth yet)
  guestApplicationId: null,
  guestSaveEmail: null,
  
  // Referral tracking
  referralCode: null,
  referrer: null, // Full referrer partner info
  referralId: null, // ID of the referral record

  // V1 product matrix (triage → product pick → per-product fields)
  v1: {
    phase: 'triage',
    triageAnswers: {},
    fieldAnswers: {},
    productId: null,
    exitId: null,
  },
};

const loanApplicationSlice = createSlice({
  name: 'loanApplication',
  initialState,
  reducers: {
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    
    updateProductRecommendation: (state, action) => {
      state.productRecommendation = {
        ...state.productRecommendation,
        ...action.payload,
      };
      // Calculate readiness_score based on answers
      state.readinessScore = calculateReadinessScore(state);
    },
    
    setRecommendedProduct: (state, action) => {
      state.recommendedProduct = action.payload;
    },
    
    setRecommendedProducts: (state, action) => {
      state.recommendedProducts = action.payload;
    },
    
    updateApplicationData: (state, action) => {
      // Support both formats: { section, data } or just flat data object
      if (action.payload.section && action.payload.data) {
        const { section, data } = action.payload;
        // Initialize section if it doesn't exist
        if (!state.applicationData[section]) {
          state.applicationData[section] = {};
        }
        // If data contains entries array, replace it entirely (don't spread)
        if (data.entries !== undefined) {
          state.applicationData[section] = {
            ...state.applicationData[section],
            entries: data.entries,
          };
        } else {
          state.applicationData[section] = {
            ...state.applicationData[section],
            ...data,
          };
        }
      } else {
        // Flat data object - merge directly into applicationData
        state.applicationData = {
          ...state.applicationData,
          ...action.payload,
        };
      }
      // Recalculate readiness_score
      state.readinessScore = calculateReadinessScore(state);
    },
    
    addDocument: (state, action) => {
      state.documents.push(action.payload);
      // Documents increase readiness_score
      state.readinessScore = calculateReadinessScore(state);
    },
    
    removeDocument: (state, action) => {
      state.documents = state.documents.filter(doc => doc.id !== action.payload);
      state.readinessScore = calculateReadinessScore(state);
    },
    
    setUnderwritingChoice: (state, action) => {
      state.underwritingChoice = action.payload;
    },
    
    submitToLenders: (state, action) => {
      state.submittedToLenders = true;
      state.selectedLenders = action.payload;
    },
    
    saveProgress: (state) => {
      state.lastSaved = new Date().toISOString();
    },
    
    setDocumentPath: (state, action) => {
      state.documentPath = action.payload;
    },
    
    uploadDocument: (state, action) => {
      state.documentsUploaded.push(action.payload);
      state.readinessScore = calculateReadinessScore(state);
    },
    
    submitAnonymously: (state) => {
      state.anonymousSubmission = true;
    },
    
    addInterestedLender: (state, action) => {
      state.interestedLenders.push(action.payload);
    },
    
    selectLender: (state, action) => {
      state.selectedLender = action.payload;
    },
    
    completeUnderwriting: (state, action) => {
      state.underwritingComplete = true;
      state.underwritingReport = action.payload;
      state.readinessScore = action.payload.score || state.readinessScore;
    },
    
    resetApplication: () => initialState,

    /** Clear in-progress application data but keep the signed-in user (dev / start-over). */
    restartApplication: (state) => ({
      ...initialState,
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      referralCode: state.referralCode,
      referrer: state.referrer,
      referralId: state.referralId,
    }),

    /** Assessment-only reset (stale local storage / new tab) without signing the user out. */
    resetAssessmentProgress: (state) => {
      state.currentStep = STEPS.ASSESSMENT;
      state.productRecommendation = { ...initialState.productRecommendation };
      state.recommendedProduct = null;
      state.recommendedProducts = [];
      state.readinessScore = 0;
      state.v1 = {
        phase: 'triage',
        triageAnswers: {},
        fieldAnswers: {},
        productId: null,
        exitId: null,
      };
      state.applicationData = {
        ...initialState.applicationData,
        borrowerInfo: { entries: [] },
        employmentIncome: { entries: [] },
        otherIncome: { entries: [] },
        assets: { entries: [] },
        credits: { entries: [] },
        giftsGrants: { entries: [] },
        realEstateOwned: { entries: [] },
      };
    },
    
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.lastSaved = new Date().toISOString();
    },
    
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
    },
    
    setReferral: (state, action) => {
      const { referralCode, referrer, referralId } = action.payload;
      state.referralCode = referralCode;
      state.referrer = referrer;
      state.referralId = referralId;
    },
    
    clearReferral: (state) => {
      state.referralCode = null;
      state.referrer = null;
      state.referralId = null;
    },
    
    initializePrimaryBorrower: (state) => {
      // Auto-populate primary borrower from personalInfo and user data
      const personalInfo = state.applicationData.personalInfo || {};
      const contactInfo = state.applicationData.contactInfo || {};
      const user = state.user || {};
      
      // Only initialize if borrowerInfo is empty
      if (!state.applicationData.borrowerInfo?.entries?.length) {
        const primaryBorrower = {
          borrowerType: 'Primary Borrower',
          firstName: personalInfo.firstName || user.firstName || '',
          middleName: personalInfo.middleName || '',
          lastName: personalInfo.lastName || user.lastName || '',
          suffix: personalInfo.suffix || '',
          ssn: personalInfo.ssn || '',
          dateOfBirth: personalInfo.dateOfBirth || '',
          citizenship: personalInfo.citizenship || '',
          maritalStatus: personalInfo.maritalStatus || '',
          dependentsCount: personalInfo.dependents || 0,
          dependentsAges: '',
        };
        
        state.applicationData.borrowerInfo = {
          entries: [primaryBorrower],
        };
      }
    },
    
    updateV1TriageAnswers: (state, action) => {
      state.v1.triageAnswers = { ...state.v1.triageAnswers, ...action.payload };
    },

    updateV1FieldAnswers: (state, action) => {
      state.v1.fieldAnswers = { ...state.v1.fieldAnswers, ...action.payload };
    },

    setV1Phase: (state, action) => {
      state.v1.phase = action.payload;
    },

    setV1ProductId: (state, action) => {
      state.v1.productId = action.payload;
      state.v1.exitId = null;
    },

    setV1ExitId: (state, action) => {
      state.v1.exitId = action.payload;
    },

    clearV1Exit: (state) => {
      state.v1.exitId = null;
    },

    setGuestApplicationId: (state, action) => {
      state.guestApplicationId = action.payload;
    },

    setGuestSaveEmail: (state, action) => {
      state.guestSaveEmail = action.payload;
    },

    recalculateReadiness: (state) => {
      state.readinessScore = calculateReadinessScore(state);
    },

    loadApplicationData: (state, action) => {
      // Load saved application data from database
      const data = action.payload;
      
      if (data.current_step !== undefined) state.currentStep = data.current_step;
      if (data.readiness_score !== undefined) state.readinessScore = data.readiness_score;
      if (data.loan_product) {
        // Reconstruct recommended product from saved data
        state.recommendedProduct = { name: data.loan_product };
      }
      if (data.product_recommendation) state.productRecommendation = data.product_recommendation;
      if (data.personal_info) state.applicationData.personalInfo = data.personal_info;
      if (data.property_info) state.applicationData.propertyInfo = data.property_info;
      if (data.loan_details) state.applicationData.loanInfo = data.loan_details;
      if (data.employment_info) state.applicationData.employmentInfo = data.employment_info;
      if (data.income_info) state.applicationData.income = data.income_info;
      if (data.asset_info) state.applicationData.assets = data.asset_info;
      if (data.declarations) state.applicationData.declarations = data.declarations;
      if (data.underwriting_choice) state.underwritingChoice = data.underwriting_choice;
      
      state.lastSaved = data.updated_at || new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state) => {
      if (state.currentStep === STEPS.ASSESSMENT) {
        state.readinessScore = calculateReadinessScore(state);
      }
    });
  },
});

// Score calculation delegated to shared utility: src/utils/readinessScoring.js
function calculateReadinessScore(state) {
  if (state.currentStep === STEPS.ASSESSMENT) {
    return calculateAssessmentReadinessTotal(state);
  }
  return calculateReadinessTotal(state);
}

export const {
  setCurrentStep,
  updateProductRecommendation,
  setRecommendedProduct,
  setRecommendedProducts,
  updateApplicationData,
  addDocument,
  removeDocument,
  setUnderwritingChoice,
  submitToLenders,
  saveProgress,
  setDocumentPath,
  uploadDocument,
  submitAnonymously,
  addInterestedLender,
  selectLender,
  completeUnderwriting,
  resetApplication,
  resetAssessmentProgress,
  restartApplication,
  setUser,
  logout,
  setReferral,
  clearReferral,
  initializePrimaryBorrower,
  loadApplicationData,
  updateV1TriageAnswers,
  updateV1FieldAnswers,
  setV1Phase,
  setV1ProductId,
  setV1ExitId,
  clearV1Exit,
  setGuestApplicationId,
  setGuestSaveEmail,
  recalculateReadiness,
} = loanApplicationSlice.actions;

export default loanApplicationSlice.reducer;
